import { register } from "./register";
// import { AppState } from "../types";
import { dispatchCanvasEvent } from "../events";
import { getSelectedElements } from "../scene";
import { t } from "../i18n";
import type { ExcalidrawElement } from "../element/types";
import type { AppState } from "../types";

type MindmapActionAppState = Pick<
  AppState,
  "selectedElementIds" | "viewModeEnabled"
>;

/**
 * The selected elements a mindmap action may act on.
 *
 * Every one of these actions refetches from Notion and rewrites the scene, so
 * they follow the same read-only rule the renderer draws its affordances by
 * (`interactive` in `RenderMindmapNode`): nothing on a read-only surface
 * (/share, view mode), and never a locked pill.
 */
const actionableSelection = (
  elements: readonly ExcalidrawElement[],
  appState: MindmapActionAppState,
): readonly ExcalidrawElement[] =>
  appState.viewModeEnabled
    ? []
    : getSelectedElements(elements, appState).filter((el) => !el.locked);

/**
 * The DISTINCT mindmap identities ({ mapId, rootPageId }) carried by the
 * selection, in selection order. A selection can span several maps — refreshing
 * only the first one silently ignored the rest — so "Refresh from Notion" is
 * both available (predicate: at least one) and actionable (perform: all of
 * them) from any set of generated elements.
 */
const selectedMindmapIdentities = (
  elements: readonly ExcalidrawElement[],
  appState: MindmapActionAppState,
): { mapId: string; rootPageId: string }[] => {
  const byMapId = new Map<string, { mapId: string; rootPageId: string }>();
  for (const el of actionableSelection(elements, appState)) {
    const mindmap = el.customData?.mindmap as
      | { mapId?: string; rootPageId?: string }
      | undefined;
    if (mindmap?.mapId && mindmap?.rootPageId && !byMapId.has(mindmap.mapId)) {
      byMapId.set(mindmap.mapId, {
        mapId: mindmap.mapId,
        rootPageId: mindmap.rootPageId,
      });
    }
  }
  return [...byMapId.values()];
};

export const actionNotion = register({
  name: "importFromNotion",
  viewMode: false,
  trackEvent: {
    category: "canvas",
    predicate: (appState) => !appState.gridSize,
  },
  perform(elements, appState) {
    dispatchCanvasEvent("contextOptionSelected", {
      type: "importFromNotion",
    });
    return {
      // `open` here used to spread the global window.open function into
      // appState — a latent bug; the dispatched event above is the real work.
      appState,
      commitToHistory: false,
    };
  },
  predicate: (element, appState, props) => {
    //function to decide whether to show this option or not
    return typeof props.renderCommandPalette !== "undefined";
  },
  contextItemLabel: "buttons.notion", // right click option name
});

export const actionCreatePage = register({
  name: "createNotionPageForExisting",
  trackEvent: { category: "element" },
  perform: async (elements, appState, _data, app) => {
    dispatchCanvasEvent("contextOptionSelected", {
      type: "createNotionPageForExisting",
    });

    return {
      commitToHistory: false,
    };
  },
  contextItemLabel: "labels.createNotionPageForExisting",
});

export const actionRefreshMindmap = register({
  name: "refreshFromNotion",
  trackEvent: { category: "element" },
  perform: async (elements, appState) => {
    // The refresh needs the Notion fetch layer + imperative API, which live in
    // the app shell, not this core package. Hand off via a canvas event; the
    // shell runs diff + apply against the current scene. One event per map, so
    // a selection spanning several maps refreshes all of them.
    for (const identity of selectedMindmapIdentities(elements, appState)) {
      dispatchCanvasEvent("refreshMindmapFromNotion", identity);
    }
    return {
      commitToHistory: false,
    };
  },
  predicate: (elements, appState) =>
    selectedMindmapIdentities(elements, appState).length > 0,
  contextItemLabel: "mindmap.refresh",
});

/**
 * The element id of the first selected mindmap node (a `mindmapNode` carrying a
 * mindmap identity), or null. "Expand all" targets that node's subtree — on the
 * root, the whole map.
 */
const selectedMindmapNodeId = (
  elements: readonly ExcalidrawElement[],
  appState: MindmapActionAppState,
): string | null => {
  for (const el of actionableSelection(elements, appState)) {
    const mindmap = el.customData?.mindmap as { mapId?: string } | undefined;
    if (el.type === "mindmapNode" && mindmap?.mapId) {
      return el.id;
    }
  }
  return null;
};

export const actionExpandAllMindmap = register({
  name: "expandAllMindmap",
  trackEvent: { category: "element" },
  perform: async (elements, appState) => {
    const elementId = selectedMindmapNodeId(elements, appState);
    if (elementId) {
      // Expansion needs the Notion fetch layer + imperative API in the app
      // shell; hand off via a canvas event, like refresh.
      dispatchCanvasEvent("mindmapExpandAll", { elementId });
    }
    return {
      commitToHistory: false,
    };
  },
  predicate: (elements, appState) =>
    selectedMindmapNodeId(elements, appState) !== null,
  contextItemLabel: "mindmap.expandAll",
});

/**
 * The selected map ROOT pill whose deletion ledger is non-empty, plus the entry
 * count, or null.
 *
 * Only the root offers a restore: the ledger lives on the root element, it is
 * map-wide, and offering it on every pill would read as "restore under this
 * node". The stamp is read structurally — the core package cannot import the
 * app's mindmap types — and both ledger shapes are accepted, so a scene saved
 * before the ledger became entry-shaped still offers the action.
 */
const selectedMindmapRestoreTarget = (
  elements: readonly ExcalidrawElement[],
  appState: MindmapActionAppState,
): { mapId: string; rootPageId: string; count: number } | null => {
  for (const el of actionableSelection(elements, appState)) {
    const mindmap = el.customData?.mindmap as
      | {
          mapId?: string;
          rootPageId?: string;
          blockId?: string;
          deletions?: unknown[];
          deletedBlockIds?: unknown[];
        }
      | undefined;
    if (
      el.type !== "mindmapNode" ||
      !mindmap?.mapId ||
      !mindmap.rootPageId ||
      mindmap.blockId !== mindmap.rootPageId
    ) {
      continue;
    }
    const count =
      mindmap.deletions?.length || mindmap.deletedBlockIds?.length || 0;
    if (count > 0) {
      return { mapId: mindmap.mapId, rootPageId: mindmap.rootPageId, count };
    }
  }
  return null;
};

export const actionRestoreMindmapDeletions = register({
  name: "restoreMindmapDeletions",
  trackEvent: { category: "element" },
  perform: async (elements, appState) => {
    const target = selectedMindmapRestoreTarget(elements, appState);
    if (target) {
      // Clearing the ledger and refetching both need the app shell (the Notion
      // fetch layer + imperative API), so hand off via a canvas event.
      dispatchCanvasEvent("mindmapRestoreDeleted", {
        mapId: target.mapId,
        rootPageId: target.rootPageId,
      });
    }
    return {
      commitToHistory: false,
    };
  },
  predicate: (elements, appState) =>
    selectedMindmapRestoreTarget(elements, appState) !== null,
  // Already-translated: the count has to be interpolated here, and the context
  // menu falls back to the returned string when it is not a translation key.
  contextItemLabel: (elements, appState) =>
    t("mindmap.restoreDeleted", {
      count: selectedMindmapRestoreTarget(elements, appState)?.count ?? 0,
    }),
});

/**
 * The selected DATABASE map root pill, or null. Regrouping only exists for a
 * database map (a page map has no properties to group by) and only on its root
 * (a row is not the thing being organised). Read structurally, like the restore
 * target: the core package cannot import the app's mindmap types.
 */
const selectedDatabaseRootId = (
  elements: readonly ExcalidrawElement[],
  appState: MindmapActionAppState,
): string | null => {
  for (const el of actionableSelection(elements, appState)) {
    const mindmap = el.customData?.mindmap as
      | {
          mapId?: string;
          rootPageId?: string;
          blockId?: string;
          source?: string;
        }
      | undefined;
    if (
      el.type === "mindmapNode" &&
      mindmap?.mapId &&
      mindmap.source === "database" &&
      mindmap.blockId === mindmap.rootPageId
    ) {
      return el.id;
    }
  }
  return null;
};

export const actionGroupMindmapBy = register({
  name: "groupMindmapBy",
  trackEvent: { category: "element" },
  perform: async (elements, appState) => {
    const elementId = selectedDatabaseRootId(elements, appState);
    if (elementId) {
      // The chooser itself is the root pill's hover popover, which lives in the
      // app shell — open it there rather than duplicating the list in a menu.
      dispatchCanvasEvent("mindmapOpenGroupBy", { elementId });
    }
    return {
      commitToHistory: false,
    };
  },
  predicate: (elements, appState) =>
    selectedDatabaseRootId(elements, appState) !== null,
  contextItemLabel: "mindmap.groupBy",
});

export const actionLinkPage = register({
  name: "linkNotionPageForExisting",
  trackEvent: { category: "element" },
  perform: async (elements, appState, _data, app) => {
    dispatchCanvasEvent("contextOptionSelected", {
      type: "linkNotionPageForExisting",
    });

    return {
      commitToHistory: false,
    };
  },
  contextItemLabel: "labels.linkNotionPageForExisting",
});
